import { Button, Typography, Table, Modal, notification, Input } from "antd"
import { CloseCircleOutlined, DeleteOutlined, EyeOutlined, UserOutlined, GiftOutlined, SearchOutlined } from "@ant-design/icons"
import { Filter } from "../../../components";
import { useEffect, useState, useCallback, useRef } from "react";
import ajaxService from "../../../services/ajax-service"
import moment from "moment";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";

const { Text, Title } = Typography

const Referrals = () => {
    const [filter, setFilter] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const debounceTimer = useRef(null)
    const [showModal, setShowModal] = useState(false)
    const [data, setData] = useState([])
    const [dataSource, setDataSource] = useState({})
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

    const handleClick = (id) => {

        const findData = (dataSource.data).find(i => i.id === id);

        setData(findData.referred_users)
        setShowModal(true)
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            ellipsis: true,
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            sortDirections: ['ascend', 'descend'],
            render: (name) => <Text style={{ fontSize: '0.85rem' }}>{name}</Text>,
        },
        {
            title: 'Created Date',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            sorter: (a, b) => {
                if (!a.created_at && !b.created_at) return 0;
                if (!a.created_at) return 1;
                if (!b.created_at) return -1;
                return moment(a.created_at).unix() - moment(b.created_at).unix();
            },
            sortDirections: ['ascend', 'descend'],
            render: (created_at) => (<Text style={{ fontSize: '0.85rem' }}>{created_at ? moment(created_at).format('DD-MMM-YYYY') : ''}</Text>)
        },
        {
            title: 'Referral Code',
            dataIndex: 'referrral_code',
            key: 'referrral_code',
            width: 150,
            ellipsis: true,
            render: (code) => <Text style={{ fontSize: '0.85rem' }}>{code}</Text>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 140,
            fixed: 'right',
            render: (text, record) => (
                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                    <Button 
                        type="text"
                        size="small" 
                        style={{ 
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: 'var(--primary-purple)',
                            border: '1px solid var(--primary-purple)',
                            borderRadius: 'var(--radius-md)',
                            transition: 'var(--transition-fast)'
                        }} 
                        icon={<EyeOutlined style={{ fontSize: '12px' }} />}
                        onClick={() => handleClick(record.id)}
                    />
                    <Button 
                        type="text"
                        size="small" 
                        style={{ 
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: 'var(--error-red)',
                            border: '1px solid var(--error-red)',
                            borderRadius: 'var(--radius-md)',
                            transition: 'var(--transition-fast)'
                        }} 
                        icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
                        onClick={() => console.log(record.id)}
                    />
                </div>
            )
        },
    ];

    const handleChangePage = async (page) => {
        const response = await ajaxService.get(`/get-referrals?name=${filter}&page=${page}`);
        if (response.success) {
            setDataSource(response)
        }

    }

    const handleSearch = async ({ name }) => {
        const searchName = name ? name.trim() : '';
        const response = await ajaxService.get(`/get-referrals?name=${searchName}`);

        const { success, data } = response;

        if (success) {
            if (data.length == 0 && searchName) {
                setFilter('')
                notification.open({ message: `${searchName} is not found!`, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> })
            } else {
                setFilter(searchName)
            }

            setDataSource(response)
        }
    }

    // Debounced search handler
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        debounceTimer.current = setTimeout(() => {
            handleSearch({ name: searchValue })
        }, 500)

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
        }
    }, [searchValue])


    const initialize = async () => {
        const response = await ajaxService.get(`/get-referrals?name=${filter}`);
        if (response.success) {
            setDataSource(response)
        }
    }

    useEffect(() => {
        initialize()
    }, []);

    // Listen for sidebar collapse state
    useEffect(() => {
        const handleSidebarCollapse = (event) => {
            setSidebarCollapsed(event.detail.collapsed);
        };

        window.addEventListener('sidebarCollapse', handleSidebarCollapse);
        return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    }, []);

    // Calculate table scroll width using standardized utility
    // Table columns: Name(200) + Created(150) + Referral Code(150) + Actions(140) = 640px
    const calculateTableWidth = useCallback(() => {
        return calculateTableScrollX(sidebarCollapsed, 500, 700, 40);
    }, [sidebarCollapsed]);

    const [tableWidth, setTableWidth] = useState(() => calculateTableScrollX(false, 500, 700, 40));

    // Recalculate on window resize and sidebar toggle
    useEffect(() => {
        const handleResize = () => {
            setTableWidth(calculateTableWidth());
        };

        window.addEventListener('resize', handleResize);
        handleResize();
        
        handleResize(); // Initial calculation
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateTableWidth]);

    const renderModal = (
        <Modal
            className="modal-overlay"
            width={calculateModalWidth('400px')}
            open={showModal}
            maskClosable={false}
            onCancel={() => setShowModal(false)}
            footer={null}
            centered
        >
            <div style={{ 
                padding: "var(--spacing-2xl) var(--spacing-xl)",
                background: 'var(--dark-bg-card)',
                borderRadius: 'var(--radius-lg)'
            }}>
                {/* Modal Header */}
                <div style={{ 
                    marginBottom: 'var(--spacing-xl)', 
                    textAlign: "center",
                    paddingBottom: 'var(--spacing-lg)',
                    borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
                }}>
                    <Title id='modal-title' level={3} style={{ 
                        color: 'var(--text-white)', 
                        margin: 0,
                        fontFamily: 'Poppins-Bold',
                        fontSize: '1.5rem'
                    }}>
                        {data.length} Users Invited
                    </Title>
                </div>
                
                {/* Users List */}
                <div style={{ 
                    maxHeight: '400px',
                    overflowY: 'auto',
                    padding: 'var(--spacing-md) 0'
                }}>
                    {data.map(i => (
                        <div key={i.id} style={{ 
                            padding: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-sm)',
                            background: 'rgba(254, 2, 125, 0.05)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid rgba(254, 2, 125, 0.2)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ color: 'var(--text-white)', fontWeight: 500 }}>{i.user_name}</Text>
                                <Text style={{ color: 'var(--primary-purple)', fontSize: '0.85rem' }}>
                                    {i.created_at ? moment(i.created_at).format('DD-MMM-YYYY') : ''}
                                </Text>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Modal Footer */}
                <div style={{ 
                    marginTop: 'var(--spacing-xl)',
                    paddingTop: 'var(--spacing-lg)',
                    borderTop: '1px solid rgba(254, 2, 125, 0.2)',
                    textAlign: 'center'
                }}>
                    <Button
                        onClick={() => setShowModal(false)}
                        className="btn-primary"
                        style={{
                            minWidth: '120px',
                            height: '40px'
                        }}
                    >
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    )

    return (
        <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Header Section */}
            <div className="module-header">
                <h1 className="module-title">Referral Management</h1>
                <p className="module-subtitle">Manage and monitor user referrals and invitations</p>
            </div>

            {/* Statistics Cards */}
            <div className="stats-grid">
                <div className="stats-card">
                    <div className="stats-value">{dataSource.meta?.total || 0}</div>
                    <div className="stats-label">Total Referrals</div>
                    <UserOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{dataSource.data?.filter(r => r.referred_users?.length > 0).length || 0}</div>
                    <div className="stats-label">Active Referrals</div>
                    <GiftOutlined style={{ color: 'var(--accent-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{dataSource.data?.reduce((sum, r) => sum + (r.referred_users?.length || 0), 0) || 0}</div>
                    <div className="stats-label">Total Invites</div>
                    <UserOutlined style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{dataSource.data?.filter(r => {
                                const created = moment(r.created_at);
                                const now = moment();
                                return created.isSame(now, 'month') && created.isSame(now, 'year');
                    }).length || 0}</div>
                    <div className="stats-label">This Month</div>
                    <GiftOutlined style={{ color: 'var(--text-gray)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
            </div>

            {/* Filters and Search */}
            <div className="filter-section">
                <div className="filter-row">
                    <div className="filter-item">
                        <Input
                            placeholder="Search referrals..."
                            prefix={<SearchOutlined style={{ color: 'var(--text-gray-light)' }} />}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onPressEnter={(e) => {
                                setSearchValue(e.target.value);
                                handleSearch({ name: e.target.value });
                            }}
                            className="input-field"
                            allowClear
                        />
                    </div>
                </div>
            </div>

            {/* Referrals Table */}
            <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                <Table
                    columns={columns}
                    dataSource={dataSource.data}
                    className="data-table"
                    rowKey='id'
                    pagination={{
                        onChange: (page, size) => handleChangePage(page),
                        current: dataSource.meta?.current_page,
                        pageSize: dataSource.meta?.per_page,
                        total: dataSource.meta?.total,
                        className: "data-table-pagination",
                        showSizeChanger: false,
                        showQuickJumper: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} referrals`,
                        size: 'small'
                    }}
                    scroll={{ x: tableWidth }}
                    size="small"
                    bordered={false}
                />
            </div>

            {renderModal}
        </div>
    )
}

export default Referrals